import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getStoredUtmParameters } from '../utils/utmParameters';

const EventFormContext = createContext();

const STORAGE_KEY = 'eventDetails';

function loadFromStorage() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

export function EventFormProvider({ children }) {
  const [formData, setFormData] = useState(loadFromStorage);
  const debounceRef = useRef(null);

  // Sync UTM params into form data on mount so they travel with the form state.
  // This acts as a backup in case the dedicated utm_parameters key is cleared.
  useEffect(() => {
    const utm = getStoredUtmParameters();
    if (Object.keys(utm).length > 0) {
      setFormData(prev => {
        if (prev._utm && Object.keys(prev._utm).length > 0) return prev;
        return { ...prev, _utm: utm };
      });
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
      } catch (e) {
        console.warn('localStorage write failed:', e);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [formData]);

  const updateFormData = useCallback((updater) => {
    setFormData(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return { ...prev, ...next };
    });
  }, []);

  const updateSetScene = useCallback((fields) => {
    setFormData(prev => ({
      ...prev,
      setScene: { ...prev.setScene, ...fields }
    }));
  }, []);

  const clearFormData = useCallback(() => {
    setFormData({});
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const resetForNewSubmission = useCallback((occasion) => {
    const fresh = occasion ? { setScene: { occasion } } : {};
    setFormData(fresh);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
  }, []);

  const value = {
    formData,
    setFormData,
    updateFormData,
    updateSetScene,
    clearFormData,
    resetForNewSubmission
  };

  return (
    <EventFormContext.Provider value={value}>
      {children}
    </EventFormContext.Provider>
  );
}

export function useEventForm() {
  const context = useContext(EventFormContext);
  if (!context) {
    throw new Error('useEventForm must be used within an EventFormProvider');
  }
  return context;
}

export default EventFormContext;
